import type { ApiSyncCrmLevelPort } from '~/handlers/ports/api-sync-crm-level.port';
import type {
  CrmLevelSyncErrorKind,
  CrmLevelSyncResult,
} from '~/types/crm-level-sync';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoAuthHeaders } from '~/utils/apostatudo/get-apostatudo-auth-headers';

// Erro tipado no mesmo desenho do RouletteSpinError/DailyCheckinClaimError: o
// adapter só CLASSIFICA a recusa; quem decide o que fazer (aqui: nunca
// retentar, só marcar a sessão) é a store.
export class CrmLevelSyncError extends Error {
  constructor(
    public readonly kind: CrmLevelSyncErrorKind,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'CrmLevelSyncError';
  }
}

const CONFIG_GAP_CODES = new Set([
  'vip_not_on_ladder',
  'vip_tier_not_mirrored',
  'level_not_mapped',
]);

export function classifySyncError(
  status: number,
  code: unknown,
): CrmLevelSyncErrorKind {
  if (status === 404) return 'player_not_found';
  if (status === 422 && typeof code === 'string' && CONFIG_GAP_CODES.has(code))
    return 'config_gap';
  if (status === 429) return 'rate_limited';
  if (status === 503 && code === 'lookup_budget_exhausted')
    return 'budget_exhausted';
  if (status === 401) return 'unauthenticated';

  return 'api';
}

// POST /crm/users/{userId}/sync-level — pede ao core pra realinhar o nível que
// ele guarda com o que a Smartico reporta.
//
// Autenticação é o Bearer do JOGADOR (getApostatudoAuthHeaders, o mesmo das
// chamadas logadas de promocode/roleta/check-in) — NUNCA o token de integração
// da marca. Identidade vai na URL, sem body.
//
// A fonte tem cache de 5 min por jogador e a rota tem rate limit próprio: este
// adapter não retenta NADA — nem rede, nem 429. Uma tentativa, um veredito.
export class ApostatudoApiPostSyncCrmLevelAdapter
  implements ApiSyncCrmLevelPort
{
  async handle({ userId }: { userId: number }): Promise<CrmLevelSyncResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let response: Response;

    try {
      response = await fetch(
        `${getApostatudoApiUrl()}/crm/users/${userId}/sync-level`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            ...getApostatudoAuthHeaders(),
          },
          signal: controller.signal,
        },
      );
    } catch {
      // Timeout (AbortError) ou queda de rede (TypeError).
      throw new CrmLevelSyncError('network');
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));

      throw new CrmLevelSyncError(
        classifySyncError(response.status, err?.error),
        err?.message,
      );
    }

    // 200 ou 201: { user_id, status, level, vip_pinned }.
    const body = await response.json().catch(() => ({}));

    return {
      userId: typeof body?.user_id === 'number' ? body.user_id : userId,
      status:
        body?.status === 'created' || body?.status === 'updated'
          ? body.status
          : 'unchanged',
      level: typeof body?.level === 'string' && body.level ? body.level : null,
      vipPinned: body?.vip_pinned === true,
    };
  }
}
